import { Link } from 'react-router-dom';
import { Users } from 'lucide-react';
import SEO from '@/components/SEO';

const EFFECTIVE_DATE = 'May 19, 2026';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="text-xl font-bold text-gray-900 dark:text-zinc-100 mb-3">{title}</h2>
      <div className="text-gray-700 dark:text-zinc-300 space-y-3 leading-relaxed">{children}</div>
    </section>
  );
}

export default function CommunityGuidelinesPage() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-zinc-950">
      <SEO
        title="Community Guidelines"
        description="Open Card Vault community guidelines. Learn how to be a respectful member of our trading card collector community."
        url="/legal/community-guidelines"
      />
      <div className="bg-gradient-to-r from-brand-600 to-accent-600 text-white py-12">
        <div className="container-custom">
          <div className="flex items-center gap-3 mb-2">
            <Users className="h-7 w-7" />
            <h1 className="text-3xl font-display font-bold">Community Guidelines</h1>
          </div>
          <p className="text-brand-100">Effective date: {EFFECTIVE_DATE}</p>
        </div>
      </div>

      <div className="container-custom py-10 max-w-3xl">
        <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-8">

          <p className="text-gray-700 dark:text-zinc-300 mb-8 leading-relaxed">
            Open Card Vault is a community and marketplace for trading-card collectors. These
            Community Guidelines exist to protect users, maintain marketplace integrity, promote
            safe participation, and provide a welcoming environment for collectors. These
            Guidelines apply to all users, content, listings, messages, reviews, forums,
            transactions, trades, and interactions conducted through Open Card Vault.
            Violations may result in content removal, feature restrictions, marketplace
            restrictions, account suspension, or permanent account termination.
          </p>

          <Section title="1. Age and Marketplace Restrictions">
            <p>Users must be at least 13 years old to create an Open Card Vault account.</p>
            <p>Users under 18 may use only those non-transactional features made available to minor users.</p>
            <p>Users under 18 may not:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Buy or sell items</li>
              <li>Create marketplace listings</li>
              <li>Place bids or participate in auctions</li>
              <li>Make or accept purchase offers</li>
              <li>Send or accept card-for-card trades</li>
              <li>Exchange physical property with other users through the Service</li>
              <li>Receive payments or payouts</li>
              <li>Create seller accounts</li>
              <li>Connect payment or payout accounts</li>
              <li>Attempt to circumvent marketplace age restrictions</li>
            </ul>
            <p>Users may not provide false age information or assist another user in circumventing age restrictions.</p>
          </Section>

          <Section title="2. Respect Every User">
            <p>
              Treat other members with respect regardless of experience level, collection size,
              identity, background, or opinion.
            </p>
            <p>You may not:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Harass, bully, threaten, stalk, or intimidate other users</li>
              <li>Engage in targeted harassment campaigns or pile-ons</li>
              <li>Use slurs or engage in hateful or discriminatory conduct</li>
              <li>Encourage violence or serious harm</li>
              <li>Share or threaten to share another person's private information without authorization</li>
              <li>Attempt to humiliate, blackmail, extort, or coerce another user</li>
              <li>Repeatedly contact users who have clearly asked you to stop</li>
              <li>Encourage others to harass or target another person</li>
            </ul>
            <p>
              Disagreements about cards, prices, grading, collecting, games, or other topics do
              not justify abusive behavior.
            </p>
          </Section>

          <Section title="3. Protecting Minors">
            <p>Open Card Vault has zero tolerance for the sexual exploitation, grooming, or inappropriate sexual solicitation of minors.</p>
            <p>Users may not:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Sexually solicit users under 18</li>
              <li>Send sexual content to minors</li>
              <li>Request sexual images or sexually explicit communications from minors</li>
              <li>Attempt to groom or exploit minors</li>
              <li>Encourage minors to conceal communications from parents, guardians, or authorities for inappropriate purposes</li>
              <li>Attempt to move communications with minors off-platform for sexual, exploitative, or otherwise inappropriate purposes</li>
              <li>Threaten, blackmail, coerce, or manipulate minors</li>
              <li>Facilitate or promote child sexual exploitation</li>
            </ul>
            <p>
              Apparent child sexual-exploitation material or credible threats to the safety of
              minors may be reported to appropriate authorities or organizations as required or
              permitted by law.
            </p>
          </Section>

          <Section title="4. Honest Listings and Transactions">
            <p>Marketplace activity depends on accurate information and good-faith participation.</p>
            <p>Users must:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Accurately describe item condition</li>
              <li>Use clear photographs that reasonably represent the actual item</li>
              <li>Disclose significant flaws, creases, damage, ink, alterations, restoration, trimming, recoloring, or other material defects</li>
              <li>Accurately identify editions, sets, languages, printings, rarity, grading status, and other material characteristics</li>
              <li>Disclose known authenticity concerns</li>
              <li>Only list or trade items they own or have the legal right to transfer</li>
              <li>Honor completed sales, accepted offers, valid trades, and legitimate auction results</li>
              <li>Communicate honestly concerning shipping, delays, and transaction problems</li>
            </ul>
            <p>Users may not intentionally conceal information that would materially affect another user's decision to enter a transaction.</p>
          </Section>

          <Section title="5. Counterfeits, Fraud, and Scams">
            <p>Fraudulent activity may result in immediate permanent account termination.</p>
            <p>Users may not:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Sell or trade counterfeit items as authentic</li>
              <li>Knowingly misrepresent altered, fake, proxy, or reproduction cards</li>
              <li>Accept payment and intentionally fail to ship</li>
              <li>Ship items materially different from those purchased or traded</li>
              <li>Create false transactions</li>
              <li>Use alternate accounts to create fraudulent reviews, ratings, sales, bids, or activity</li>
              <li>Submit fake payment confirmations</li>
              <li>Engage in phishing</li>
              <li>Impersonate buyers, sellers, moderators, Open Card Vault staff, payment processors, shipping providers, or other organizations</li>
              <li>Manipulate transactions, refunds, disputes, chargebacks, or payment systems</li>
              <li>Steal accounts, payment information, personal information, or other property</li>
              <li>Use the Service to launder money or facilitate illegal financial activity</li>
            </ul>
            <p>
              Open Card Vault may preserve transaction records and cooperate with payment
              processors or appropriate authorities when investigating suspected fraud.
            </p>
          </Section>

          <Section title="6. Auctions and Marketplace Manipulation">
            <p>Users may not manipulate prices, auctions, rankings, reviews, marketplace activity, or other platform systems.</p>
            <p>Prohibited conduct includes:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Shill bidding</li>
              <li>Bidding on your own listings through alternate accounts</li>
              <li>Coordinated bidding intended to artificially increase prices</li>
              <li>Bid shielding</li>
              <li>False sales intended to manipulate price history</li>
              <li>Creating fake transactions</li>
              <li>Manipulating reviews or ratings</li>
              <li>Coordinating with other users to distort market prices</li>
              <li>Using bots, scripts, or automated systems to gain unfair marketplace advantages</li>
              <li>Intentionally interfering with another user's legitimate transaction</li>
            </ul>
          </Section>

          <Section title="7. Off-Platform Transactions and Fee Avoidance">
            <p>
              Users may not use Open Card Vault to locate buyers, sellers, or traders and then
              move transactions off-platform for the purpose of avoiding platform fees, transaction
              rules, or marketplace protections.
            </p>
            <p>Users may not:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Request off-platform payment to avoid fees</li>
              <li>Post payment handles for the purpose of circumventing Open Card Vault transactions</li>
              <li>Direct users to external marketplaces to complete transactions initiated through Open Card Vault for the purpose of fee avoidance</li>
              <li>Pressure users to communicate off-platform to evade moderation, transaction records, or marketplace protections</li>
            </ul>
          </Section>

          <Section title="8. Prohibited Items">
            <p>Users may not list, sell, trade, solicit, or distribute:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Counterfeit trading cards represented as authentic</li>
              <li>Stolen property</li>
              <li>Items the user does not own or have the legal right to transfer</li>
              <li>Illegal goods or services</li>
              <li>Weapons, explosives, illegal drugs, controlled substances, or unrelated regulated products</li>
              <li>Pornographic or sexually explicit products</li>
              <li>Products promoting terrorist organizations, violent extremist organizations, or unlawful hate-based activity</li>
              <li>Products that infringe intellectual-property rights</li>
              <li>Recalled products where resale is prohibited</li>
              <li>Items prohibited by payment processors, shipping providers, or applicable law</li>
              <li>Items Open Card Vault determines present unreasonable legal, fraud, safety, or operational risks</li>
            </ul>
            <p>
              Open Card Vault may establish additional category-specific requirements or
              restrictions for particular types of items at any time.
            </p>
          </Section>

          <Section title="9. Spam and Unsolicited Communications">
            <ul className="list-disc pl-5 space-y-1">
              <li>Do not send unsolicited bulk messages to other users</li>
              <li>Do not post the same listing or content repeatedly in a short period</li>
              <li>Do not use messaging, forums, or listings to advertise external services</li>
              <li>Do not post irrelevant links, referral codes, or promotional content</li>
              <li>Do not send communications intended to deceive, phish, or misdirect users</li>
            </ul>
          </Section>

          <Section title="10. Forum and Community Spaces">
            <ul className="list-disc pl-5 space-y-1">
              <li>Post in the appropriate category and stay on topic</li>
              <li>Contribute constructively and in good faith</li>
              <li>Do not revive very old threads with unrelated content</li>
              <li>Do not brigade, vote-manipulate, or coordinate to suppress legitimate discussion</li>
              <li>Do not create new threads to contest a moderation decision — contact support instead</li>
              <li>Moderator decisions are final absent a successful appeal</li>
            </ul>
          </Section>

          <Section title="11. Reporting Violations">
            <p>
              If you see content or behavior that violates these Guidelines, use the{' '}
              <strong>Report button</strong> found on listings, profiles, messages, and forum
              posts. Do not attempt to resolve violations yourself by retaliating.
            </p>
            <p>
              Reports are reviewed by our moderation team. We keep your report confidential. Do
              not submit false, bad-faith, or retaliatory reports.
            </p>
          </Section>

          <Section title="12. Consequences for Violations">
            <p>Depending on the severity and frequency of the violation, consequences include:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Warning</strong> — For minor first-time violations</li>
              <li><strong>Content removal</strong> — Offending listings, messages, or posts removed</li>
              <li><strong>Feature restriction</strong> — Marketplace or messaging access limited</li>
              <li><strong>Temporary suspension</strong> — Account access restricted for a period</li>
              <li><strong>Permanent termination</strong> — Reserved for serious violations, fraud, severe harassment, and repeat offenders</li>
            </ul>
            <p>
              Serious violations — including counterfeit sales, fraud, grooming or exploitation of
              minors, and severe harassment — may result in immediate permanent termination without
              prior warning. Open Card Vault reserves the right to report illegal activity to
              appropriate authorities.
            </p>
          </Section>

          <Section title="13. Appeals">
            <p>
              If you believe a moderation action was taken in error, you may appeal by emailing{' '}
              <a href="mailto:support@opencardvault.com" className="text-brand-600 dark:text-brand-400 hover:underline">
                support@opencardvault.com
              </a>{' '}
              with "Appeal" in the subject line. Provide a clear explanation and any relevant
              evidence. Appeals are reviewed within 7 business days.
            </p>
          </Section>

          <div className="bg-brand-50 dark:bg-brand-900/20 border border-brand-200 dark:border-brand-800 rounded-xl p-5 mt-6">
            <p className="text-sm text-brand-800 dark:text-brand-300">
              <strong>Remember:</strong> These guidelines exist to protect everyone in the
              community. A fair, honest marketplace benefits every collector. Thank you for
              helping us keep Open Card Vault great.
            </p>
          </div>

          <div className="border-t border-gray-200 dark:border-zinc-800 pt-6 mt-8 flex flex-wrap gap-4 text-sm text-gray-500 dark:text-zinc-500">
            <Link to="/legal/privacy" className="hover:text-brand-600 dark:hover:text-brand-400">Privacy Policy</Link>
            <Link to="/legal/terms" className="hover:text-brand-600 dark:hover:text-brand-400">Terms of Service</Link>
            <Link to="/guide" className="hover:text-brand-600 dark:hover:text-brand-400">How It Works</Link>
            <Link to="/contact" className="hover:text-brand-600 dark:hover:text-brand-400">Contact Us</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
